import React, { Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import ToC from 'Components/ToC';
import { useClassroomInterruption, useSectionData } from 'Util/Hooks';
import { useClassroomCourseData } from 'Domain/Classroom';
import { PreviewModal, PREVIEW_MESSAGE_TYPE } from 'Containers/Preview';
import AccessContentModal, { MESSAGE_TYPE } from 'Components/AccessContentModal';
import { localeDate } from 'Util/datetime';
import { sendDataToGTM, GTM_EVENT_NAME, GTM_DATA_LAYER_PROPERTY } from 'Util/Analytics';

const CourseToC = ({ t, sections, onSubsectionClick, ...props }) => {
  const { isClassroomTrialMode, data: classroomCourseData } = useClassroomCourseData();
  const { isInterrupting, activateInterruption, deactivateInterruption } =
    useClassroomInterruption();
  const { data } = useSectionData({ sections });
  const modalContentRef = useRef(null);

  const handleSubsectionClick = ({ section, subsection }) => {
    const canPreviewSubsection = subsection?.canPreview;
    if (isClassroomTrialMode && !canPreviewSubsection) {
      modalContentRef.current = { messageType: PREVIEW_MESSAGE_TYPE.LOCKED };
      activateInterruption();

      const courseTitle = classroomCourseData?.classroomCourse?.title;

      // logging GTM event
      sendDataToGTM({
        event: GTM_EVENT_NAME.TRIAL_SUBSECTION_CLICK,
        [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: courseTitle,
        [GTM_DATA_LAYER_PROPERTY.SUBSECTION_TITLE]: subsection?.title,
      });

      return;
    }

    const releaseDate = subsection?.releasedAt ? dayjs(subsection.releasedAt) : null;
    const isContentAvailable = releaseDate ? dayjs().isAfter(releaseDate) : true;
    if (!isContentAvailable || subsection.isLock) {
      const description = releaseDate
        ? `บทเรียนนี้สามารถเริ่มเรียนได้ ${localeDate(releaseDate, 'th', 'DD MMMM BBBB')}`
        : null;
      modalContentRef.current = {
        description: description,
        messageType: MESSAGE_TYPE.CONTENT_UNAVAILABLE,
      };
      activateInterruption();
      return;
    }

    onSubsectionClick(subsection?.id, subsection?.referId, subsection?.title);
  };

  const messageType = modalContentRef.current?.messageType;
  return (
    <Fragment>
      <ToC sections={data} onSubsectionClick={handleSubsectionClick} />
      <PreviewModal
        previewModalType={messageType}
        isInterrupting={isInterrupting && messageType === PREVIEW_MESSAGE_TYPE.LOCKED}
        deactivateInterruption={deactivateInterruption}
      />
      <AccessContentModal
        muiProps={{
          isActive: isInterrupting && messageType === MESSAGE_TYPE.CONTENT_UNAVAILABLE,
          onClose: deactivateInterruption,
        }}
        messageType={messageType}
        description={modalContentRef.current?.description}
        onClick={deactivateInterruption}
      />
    </Fragment>
  );
};

CourseToC.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.string,
      subsections: PropTypes.arrayOf(
        PropTypes.shape({
          subsectionIndex: PropTypes.number,
          complete: PropTypes.bool,
          title: PropTypes.string,
          duration: PropTypes.string,
          active: PropTypes.bool,
          id: PropTypes.string,
        })
      ),
    })
  ),
  onSubsectionClick: PropTypes.func,
  sectionProps: PropTypes.object,
};

CourseToC.defaultProps = {
  sections: [],
};

export default withTranslation('classroom')(CourseToC);
